// MACHINERY V2 (Stroje) — the catalog browse page's PAGE-LEVEL overlays
// (split out of CatalogBrowseV2.tsx at M2.16c).
//
// >>> WHY THIS FILE EXISTS. <<< CatalogBrowseV2 stood at 296 of the project's
// 300-line cap and M2.16c must add the below-1280 chrome branch to it. The
// project rule is that a sub-phase editing a near-cap file SPLITS FIRST, as a
// discrete step, before any feature line lands. This is that split, and it is
// the same seam fleet/FleetOverlays.tsx took from FleetPage.tsx at M2.13 — so
// the two browse surfaces are now shaped alike.
//
// WHAT IT OWNS: everything the page mounts BESIDE its content — the readiness
// popup, the add dialog/sheet, the readiness legend, the toast host.
//
// >>> MOUNT DEPTH IS THE GUARD, AND IT SURVIVES THIS SPLIT. <<< These render as
// siblings of the rail/main flex row, deliberately outside every card: the
// clickable card is then not even a React ancestor of the popup, so a click on
// its Close / backdrop / CTA cannot re-enter the card's onClick and navigate.
// Same rule the page header already recorded and the same one
// FleetOverlays.tsx:10-16 keeps. The popup ALSO carries stopPropagation on both
// portalled siblings, which is what protects the in-card case. Do not nest this
// inside the grid.
//
// The DOM after this split is what it was before it: same elements, same order,
// same depth — a fragment in the same position the four mounts occupied.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { CatalogModelListItem } from "../../models/models";
import MachineryToastHost from "../feedback/MachineryToastHost";
import ReadinessLegendSheet from "../readiness/ReadinessLegendSheet";
import ReadinessPopupV2 from "../readiness/ReadinessPopupV2";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import CatalogAddDialogV2 from "./CatalogAddDialogV2";

export interface CatalogOverlaysProps {
  readinessModel: CatalogModelListItem | null;
  readinessPresentation: MachineryV2ReadinessPresentation | null;
  onCloseReadiness: () => void;
  addModel: CatalogModelListItem | null;
  onCloseAdd: () => void;
  onCreated: (id?: string) => void;
  /** Board 19 — opened from the mobile page head, absent at >= 1280. */
  legendOpen: boolean;
  onLegendOpenChange: (open: boolean) => void;
}

const CatalogOverlays: React.FC<CatalogOverlaysProps> = ({
  readinessModel,
  readinessPresentation,
  onCloseReadiness,
  addModel,
  onCloseAdd,
  onCreated,
  legendOpen,
  onLegendOpenChange,
}) => (
  <>
    {readinessModel && readinessPresentation && (
      <ReadinessPopupV2
        open
        onOpenChange={(o) => {
          if (!o) onCloseReadiness();
        }}
        presentation={readinessPresentation}
        machineOrModelName={readinessModel.modelName}
        oemPartnerName={readinessModel.oemPartnerName}
        manufacturerName={readinessModel.manufacturer?.name}
      />
    )}

    {addModel && (
      <CatalogAddDialogV2
        open
        model={addModel}
        onClose={onCloseAdd}
        onCreated={onCreated}
      />
    )}

    {/* Mounted whether or not it is open: Radix renders nothing while closed,
        and the sheet holds no user work to preserve across a close. */}
    <ReadinessLegendSheet open={legendOpen} onOpenChange={onLegendOpenChange} />

    <MachineryToastHost />
  </>
);

export default CatalogOverlays;
